import { MODEL_NAMES } from '../constants/index';
import { didCancel, task } from 'ember-concurrency';
import { filterSystemAndCurrentUser } from '../utils/messaging/user';
import { isSameConversation } from '../utils/messaging/conversation';
import Service, { service } from '@ember/service';

/**
 * Provides a high-level interface for interacting with the messaging client.
 * Consuming applications should interact with only this service.
 */
export default class MessagingInterfaceService extends Service {
  @service messaging;
  @service messagingLogging;
  @service messagingNavigation;
  @service messagingSession;
  @service store;

  get unreadConversationCount() {
    return this.messaging.currentProfile?.unreadConversationIds?.length || 0;
  }

  get isAway() {
    return this.messaging.currentProfile?.isAway || false;
  }

  get isResolvingExternalConversation() {
    return this.externalConversationNavigationTask.isRunning;
  }

  /**
   * Initializes and starts the messaging client. This task loads the messaging app
   * and sets up the necessary connections. If a new startTask is triggered while
   * a previous one is running, the previous task will be dropped.
   *
   * @param {Object} [options={}] - Configuration options
   */
  startTask = task({ drop: true }, async (options = {}) => {
    await this.messaging.loadApp.perform(options);
  });

  /**
   * Stops the messaging client by canceling token polling and closing
   * the websocket connection.
   */
  stop() {
    this.externalConversationNavigationTask.cancelAll();
    this.messaging.pollMessagingToken.cancelAll();
    this.messagingSession.stop();
  }

  externalConversationNavigationTask = task({ restartable: true }, async request => {
    try {
      const conversation = request.clientExternalIds
        ? await this.messaging.findOrCreateConversationByClientIds(
            request.clientExternalIds,
            request.coupleExternalIds
          )
        : this.#conversationReference(request.conversationId);

      if (!conversation) {
        throw new Error('Conversation could not be found');
      }

      const activeConversation = this.messagingNavigation.currentConversationHandler?.conversation;
      if (isSameConversation(conversation, activeConversation)) {
        if (this.messagingNavigation.isOnConversationThread) {
          return conversation;
        }
      }

      await this.#presentConversation(conversation);
      return conversation;
    } catch (error) {
      if (didCancel(error)) {
        throw error;
      }

      this.messagingLogging.warn(error, request);
      await this.#presentConversationList();
      throw error;
    }
  });

  /**
   * Finds or creates a conversation for the given client external IDs and
   * navigates to that conversation. If that conversation is already showing,
   * cancels in-flight external navigation and returns the live conversation.
   *
   * @param {string[]} clientExternalIds - Array of client external IDs to find or create a conversation for
   * @param {string[]} [coupleExternalIds] - Couple contact ids. When present, these identify the conversation.
   */
  goToConversationByClientIds(clientExternalIds, coupleExternalIds) {
    return this.#goToExternal(
      { clientExternalIds, coupleExternalIds },
      this.#isViewingClientConversation(clientExternalIds)
    );
  }

  /**
   * Navigates to a conversation by its ID. The conversation screen loads the
   * record. If that conversation is already showing, cancels in-flight external
   * navigation and returns the live conversation.
   *
   * @param {string} conversationId - The ID of the conversation to navigate to
   */
  goToConversationById(conversationId) {
    return this.#goToExternal(
      { conversationId },
      this.messagingNavigation.isViewingConversation(conversationId)
    );
  }

  async #goToExternal(request, alreadyShowing) {
    if (!alreadyShowing) {
      return this.externalConversationNavigationTask.perform(request);
    }

    this.externalConversationNavigationTask.cancelAll();
    const conversation = this.messagingNavigation.currentConversationHandler.conversation;
    if (this.messagingNavigation.isOnConversationThread) {
      return conversation;
    }

    await this.#presentConversation(conversation);
    return conversation;
  }

  #presentConversation(conversation) {
    this.messagingNavigation.clearHistory();
    return this.messagingNavigation.toConversation(conversation, { recordHistory: false });
  }

  #presentConversationList() {
    this.messagingNavigation.clearHistory();
    return this.messagingNavigation.toConversationList({ recordHistory: false });
  }

  #conversationReference(conversationId) {
    return (
      this.store.peekRecord(MODEL_NAMES.CONVERSATION, conversationId) ??
      this.store.push({
        data: {
          id: String(conversationId),
          type: MODEL_NAMES.CONVERSATION,
        },
      })
    );
  }

  #isViewingClientConversation(clientExternalIds) {
    const conversation = this.messagingNavigation.currentConversationHandler?.conversation;
    if (!conversation || conversation.isCouple || conversation.isGroup) {
      return false;
    }

    const users = filterSystemAndCurrentUser(
      conversation.users,
      this.messaging.currentProfile?.user
    );
    return (
      users.length === clientExternalIds.length &&
      clientExternalIds.every(externalId =>
        users.some(user => user.isClient && user.externalId === externalId)
      )
    );
  }
}
